import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Grid,
  Chip,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import TipSelection from '../components/TipSelection';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const appointmentData = location.state?.appointmentData;
  
  const [paymentMethod, setPaymentMethod] = useState<string>('payAtSalon');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [cardName, setCardName] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [cvv, setCvv] = useState<string>('');
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!appointmentData) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          No appointment data found
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/book-appointment')}
        >
          Return to Booking
        </Button>
      </Box>
    );
  }

  // Calculate subtotal from services
  const subtotal = appointmentData.services.reduce(
    (sum: number, service: any) => sum + parseFloat(service.price), 
    0
  );
  
  // Calculate total with tip
  const total = subtotal + tipAmount;

  const handleTipChange = (amount: number) => {
    setTipAmount(amount);
  };

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMethod(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      // Update the appointment with tip amount
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${appointmentData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tipAmount })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update appointment with tip');
      }
      
      // Add tip to appointment data
      const updatedAppointmentData = {
        ...appointmentData,
        tipAmount,
        totalPrice: subtotal + tipAmount
      };
      
      // Navigate to confirmation page with updated data
      navigate('/booking-confirmation', { state: { appointmentData: updatedAppointmentData } });
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      py: 4,
      px: 2,
      background: `
        linear-gradient(135deg, rgba(255, 248, 245, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%),
        url('https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '100vh'
    }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Payment
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Left column - Order Summary */}
          <Grid item xs={12} md={5}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              {appointmentData.services.map((service: any) => (
                <Box 
                  key={service.name}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography variant="body1">{service.name}</Typography>
                  <Typography variant="body1">${parseFloat(service.price).toFixed(2)}</Typography>
                </Box>
              ))}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">${subtotal.toFixed(2)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Tip</Typography>
                <Typography variant="body1">${tipAmount.toFixed(2)}</Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6" color="primary.main">${total.toFixed(2)}</Typography>
            </Box>
          </Grid>
          
          {/* Right column - Payment Form */}
          <Grid item xs={12} md={7}>
            <TipSelection subtotal={subtotal} onTipChange={handleTipChange} />
            
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payment Method
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <RadioGroup
                  name="payment-method"
                  value={paymentMethod}
                  onChange={handlePaymentMethodChange}
                >
                  <FormControlLabel 
                    value="creditCard" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CreditCardIcon sx={{ mr: 1 }} />
                        <Typography>Credit Card</Typography>
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="payAtSalon" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PaymentIcon sx={{ mr: 1 }} />
                        <Typography>Pay at Salon</Typography>
                      </Box>
                    } 
                  />
                </RadioGroup>
              </FormControl>
              
              {paymentMethod === 'creditCard' && (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Card Number"
                        variant="outlined"
                        fullWidth
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        placeholder="1234 5678 9012 3456"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Name on Card"
                        variant="outlined"
                        fullWidth
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Expiry Date"
                        variant="outlined"
                        fullWidth
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        placeholder="MM/YY"
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="CVV"
                        variant="outlined"
                        fullWidth
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value)}
                        placeholder="123"
                        required
                      />
                    </Grid>
                  </Grid>
                </form>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            endIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
            onClick={handleSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Complete Payment'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentPage;
